(ns arthur.domain.wire
  "The document's wire format, and the bytes' one.

  TRANSIT, not JSON, and the reason is the two things docs/animation-model.md is
  most specific about. A channel's keys are a map BY FRAME NUMBER, and JSON has
  only string keys, so a save through `JSON.stringify` turns `{0 v, 4 v}` into
  `{\"0\" v, \"4\" v}` and every id in the scene from `:mouth` into `\"mouth\"` — a
  document that reloads as a subtly different type and fails somewhere downstream
  of where it broke. Transit carries integers, keywords and vector keys as
  themselves, and its output is still JSON, so the server stores a leaf in a
  JSONField and the admin can read it.

  TRANSIT LOSES SORTEDNESS, which is why `domain/channel` says keys are a PLAIN
  map and builds the sorted index at read time. Nothing here re-sorts anything:
  a codec that returned a sorted map would work locally and stop working after one
  round trip, which is the failure the plain-map rule already prevents.

  The bytes are separate from transit: JSON block reads carry base64, while
  uploads send binary file parts. `channel/dense-at` reads a block as
  `{:data <typed array> :state <Uint8Array>}` and both sides of the wire must hold
  byte-for-byte the same array — a handle that names a sha256 has to name the
  bytes you actually hold."
  (:require [cognitect.transit :as t]))

(def ^:private writer (t/writer :json))
(def ^:private reader (t/reader :json))

(defn encode
  "A tier-1 value -> the transit-JSON text that goes in a leaf."
  [v]
  (t/write writer v))

(defn decode
  "The inverse. Whatever comes back is ordinary CLJS data."
  [s]
  (t/read reader s))

(defn encode-json
  "A tier-1 value -> transit as a PARSED JSON value, ready to go in a request body.

  Transit's output is a JSON string, so a leaf could travel as a string and the
  server could store it as one. It travels parsed instead, so that the column
  holding it is a JSONField holding JSON rather than a JSONField holding a string
  that happens to contain JSON. Two things need that: the admin, where a leaf is
  either readable or it is a blob, and the field-wise merge of a channel leaf that
  docs/architecture.md describes as fifteen lines of Python — which is fifteen
  lines over transit's own `[\"^ \", \"~:keys\", ...]` and impossible over an
  opaque string."
  [v]
  (js/JSON.parse (encode v)))

(defn decode-json
  "The inverse of `encode-json`."
  [json]
  (decode (js/JSON.stringify json)))

;; ---------------------------------------------------------------------------
;; the bytes

(def ^:private chunk-size
  "Not `chunk`, which is `cljs.core/chunk`. 8192 characters per `apply`."
  8192)

(defn base64
  "A typed array -> base64 of its bytes.

  Chunked through `String.fromCharCode`: `apply` with a few hundred thousand
  arguments overflows the stack, and a 600-frame geometry block is exactly that
  size. The failure is a RangeError from inside a save, which points nowhere near
  the array that caused it."
  [^js block]
  (let [bytes (js/Uint8Array. (.-buffer block) (.-byteOffset block) (.-byteLength block))
        parts (js/Array.)]
    (loop [i 0]
      (when (< i (.-length bytes))
        (.push parts (.apply js/String.fromCharCode nil (.subarray bytes i (+ i chunk-size))))
        (recur (+ i chunk-size))))
    (js/btoa (.join parts ""))))

(defn bytes-of
  "base64 -> a Uint8Array."
  [s]
  (let [binary (js/atob s)
        out    (js/Uint8Array. (.-length binary))]
    (dotimes [i (.-length binary)]
      (aset out i (.charCodeAt binary i)))
    out))

(defn typed
  "base64 -> the typed array a block of this element type is read through.

  The type is a FIELD the block carries rather than something inferred from its
  length, because an Int16Array and a Float32Array over the same bytes are both
  valid readings and only one of them is the block."
  [type s]
  (let [u8 (bytes-of s)]
    (case type
      "int16"   (js/Int16Array. (.-buffer u8))
      "float32" (js/Float32Array. (.-buffer u8))
      "float64" (js/Float64Array. (.-buffer u8))
      "uint8"   u8
      (throw (ex-info "a block's element type is \"int16\", \"float32\", \"float64\" or \"uint8\""
                      {:type type})))))
